import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, MessageCircle, CheckCircle, Wrench } from 'lucide-react';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });

  const [formStatus, setFormStatus] = useState({ submitted: false, loading: false });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitted: false, loading: true });

    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setFormStatus({ submitted: true, loading: false });
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });

      // Reset success message after 5 seconds
      setTimeout(() => {
        setFormStatus({ submitted: false, loading: false });
      }, 5000);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6" />,
      title: 'Call Us',
      detail: '+91 9923887090',
      link: 'tel:+919923887090',
      color: 'orange',
      description: 'Mon-Sun, 9 AM - 8 PM'
    },
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Us',
      detail: 'okmechanics@gmail.com',
      link: 'mailto:okmechanics@gmail.com',
      color: 'green',
      description: 'We reply within 24 hours'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Visit Us',
      detail: 'Maharashtra 411001 India',
      link: null,
      color: 'red',
      description: '6, Gen Vaidya Marg Agarkar Nagar Pune,'
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: 'Service Hours',
      detail: '9:00 AM - 8:00 PM',
      link: null,
      color: 'purple',
      description: 'Monday - Sunday'
    }
  ];

  const services = [
    'General Service',
    'Bike Repair',
    'Oil Change',
    'Brake Service',
    'Engine Repair',
    'Electrical Work',
    'Spare Parts',
    'Other'
  ];

  const features = [
    { icon: '🔧', title: 'Expert Mechanics', desc: 'Certified professionals' },
    { icon: '🚪', title: 'Doorstep Service', desc: 'Free pickup & delivery' },
    { icon: '💯', title: 'Genuine Parts', desc: '100% authentic spares' },
    { icon: '⚡', title: 'Quick Service', desc: 'Same-day delivery' }
  ];

  const inputClass = "w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#FF6B35]/30 focus:border-[#FF6B35] transition-all outline-none bg-white text-gray-900 placeholder-gray-400";

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-[#111111] text-white py-16 sm:py-20 px-4 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-full text-[#FF6B35] text-sm font-semibold mb-6">
              <span className="w-1.5 h-1.5 bg-[#FF6B35] rounded-full animate-pulse" />
              We're here to help
            </div>
            <Wrench className="w-14 h-14 mx-auto mb-6 text-[#FF6B35]" />
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black mb-6">
              Get In Touch
            </h1>
            <p className="text-lg sm:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Need bike servicing? We're here to help! Book online or reach out to our team for expert assistance.
            </p>
          </div>

          {/* Quick Features */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            {features.map((feature, index) => (
              <div key={index} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-4 text-center hover:bg-white/10 transition-all">
                <div className="text-3xl mb-2">{feature.icon}</div>
                <h3 className="font-bold text-sm mb-1 text-white">{feature.title}</h3>
                <p className="text-xs text-gray-400">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contact Info Cards */}
      <div className="max-w-7xl mx-auto px-4 -mt-10 mb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {contactInfo.map((info, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all border-t-4 border-[#FF6B35]"
            >
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center text-[#FF6B35] mb-4">
                {info.icon}
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                {info.title}
              </h3>
              {info.link ? (
                <a
                  href={info.link}
                  className="text-[#FF6B35] hover:text-[#E85520] font-semibold text-sm block mb-1 break-words"
                >
                  {info.detail}
                </a>
              ) : (
                <p className="text-gray-900 font-semibold text-sm mb-1">{info.detail}</p>
              )}
              <p className="text-xs text-gray-500">{info.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Contact Form */}
          <div className="bg-white rounded-3xl shadow-xl p-8 sm:p-10 border border-gray-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#FF6B35]/10 rounded-xl flex items-center justify-center">
                <MessageCircle className="w-6 h-6 text-[#FF6B35]" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Send Us a Message</h2>
                <p className="text-sm text-gray-600">We'll respond within 24 hours</p>
              </div>
            </div>

            {formStatus.submitted && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <p className="text-green-800 font-semibold">Message Sent Successfully!</p>
                  <p className="text-green-700 text-sm">We'll get back to you soon.</p>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Name <span className="text-[#FF6B35]">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className={inputClass}
                  placeholder="Enter your name"
                />
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email Address <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="your@email.com"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Phone Number <span className="text-[#FF6B35]">*</span>
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className={inputClass}
                    placeholder="+91 XXXXX XXXXX"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                  Service Required <span className="text-[#FF6B35]">*</span>
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className={inputClass}
                >
                  <option value="">Select a service</option>
                  {services.map((service, index) => (
                    <option key={index} value={service}>{service}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Message <span className="text-[#FF6B35]">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows="4"
                  className={`${inputClass} resize-none`}
                  placeholder="Tell us about your bike issue or service needs..."
                />
              </div>

              <button
                type="submit"
                disabled={formStatus.loading}
                className="w-full bg-[#FF6B35] hover:bg-[#E85520] text-white font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-[#FF6B35]/20 hover:shadow-[#FF6B35]/30 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:translate-y-0"
              >
                {formStatus.loading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Send Message</span>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Address Card */}
            <div className="bg-[#111111] text-white rounded-3xl shadow-xl p-8 relative overflow-hidden">
              <div
                className="absolute inset-0 opacity-[0.04]"
                style={{
                  backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
                  backgroundSize: '40px 40px',
                }}
              />
              <div className="relative">
                <h2 className="text-2xl font-bold mb-6">Visit Our Office</h2>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FF6B35]/20 rounded-xl flex items-center justify-center flex-shrink-0 text-[#FF6B35]">
                      <MapPin className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg mb-2">OkMechanic Head Office</h3>
                      <p className="text-gray-400 leading-relaxed">
                        6, Gen Vaidya Marg,<br />
                         Agarkar Nagar Pune,<br />
                        Maharashtra 411001
                      </p>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-white/10 space-y-3">
                    <a
                      href="tel:+919923887090"
                      className="flex items-center justify-center gap-2 bg-[#FF6B35] hover:bg-[#E85520] text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg shadow-[#FF6B35]/20"
                    >
                      <Phone className="w-5 h-5" />
                      <span>Call: +91 9923887090</span>
                    </a>
                    <a
                      href="mailto:okmechanics@gmail.com"
                      className="flex items-center justify-center gap-2 bg-white/5 text-white px-6 py-3 rounded-xl font-bold hover:bg-white/10 transition-all border border-white/10"
                    >
                      <Mail className="w-5 h-5" />
                      <span>Send Email</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-white rounded-3xl shadow-xl p-8 border border-gray-100">
              <h3 className="text-xl font-bold text-gray-900 mb-6">Why Choose OkMechanic?</h3>
              <div className="space-y-4">
                {[
                  { icon: '✅', text: 'Free doorstep pickup & delivery' },
                  { icon: '🔧', text: 'Certified expert mechanics' },
                  { icon: '💯', text: '100% genuine spare parts' },
                  { icon: '⚡', text: 'Same-day service available' },
                  { icon: '🛡️', text: '6-month service warranty' },
                  { icon: '💰', text: 'Transparent pricing, no hidden charges' }
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <span className="text-2xl">{item.icon}</span>
                    <span className="text-gray-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Map */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-xl border border-gray-100">
              <div className="h-64">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3783.0179!2d73.8947!3d18.5204!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTjCsDMxJzEzLjQiTiA3M8KwNTMnNDAuOSJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="OkMechanic Location - Koregaon Park, Pune"
                />
              </div>
              <div className="p-4 bg-gray-50 border-t border-gray-200">
                <p className="text-sm text-gray-600 text-center">
                  <MapPin className="w-4 h-4 inline mr-1 text-[#FF6B35]" />
                  Koregaon Park, Pune, Maharashtra
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-[#111111] py-16 px-4 mt-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg, #FF6B35 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Need Immediate Assistance?
          </h2>
          <p className="text-lg text-gray-400 mb-8">
            Our support team is available Monday to Sunday, 9 AM - 8 PM
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+919923887090"
              className="bg-[#FF6B35] hover:bg-[#E85520] text-white font-bold px-8 py-4 rounded-xl transition-all shadow-lg shadow-[#FF6B35]/20 hover:-translate-y-0.5 inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              <span>Call Now</span>
            </a>
            <a
              href="/"
              className="bg-white/5 hover:bg-white/10 text-white font-bold px-8 py-4 rounded-xl border border-white/10 transition-all inline-flex items-center justify-center gap-2"
            >
              <Wrench className="w-5 h-5" />
              <span>Book Service</span>
            </a>
          </div>
        </div>
      </div>

      {/* Emergency Support Banner */}
      <div className="bg-[#FF6B35] text-white py-6 px-4">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <p className="font-bold text-lg">24/7 Emergency Breakdown Service</p>
              <p className="text-white/80 text-sm">Available for urgent bike issues</p>
            </div>
          </div>
          <a
            href="tel:+919923887090"
            className="bg-white text-[#FF6B35] px-6 py-3 rounded-xl font-bold hover:bg-orange-50 transition-all whitespace-nowrap shadow-lg"
          >
            Emergency Call
          </a>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
